import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {version} from '../env';
import {selectUnseenNotes} from './releaseNotes';
import {RELEASE_NOTE_FILES, ReleaseNoteFile} from './noteFiles';

// The newest release-notes version whose popup the user closed. Kept per browser, not per account,
// so it survives logout. Without storage the newest note simply shows up on every load.
export const SEEN_VERSION_KEY = 'spot-drive-seen-release-notes';

function readSeenVersion(): string | null {
    try {
        return localStorage.getItem(SEEN_VERSION_KEY);
    } catch {
        return null;
    }
}

function writeSeenVersion(seen: string): void {
    try {
        localStorage.setItem(SEEN_VERSION_KEY, seen);
    } catch {
        console.log(`Failed to write new seen version to localStorage`);
    }
}

export interface ReleaseNote {
    version: string;
    /** Markdown. */
    text: string;
}

function fetchReleaseNotes(files: ReleaseNoteFile[]): Promise<ReleaseNote[]> {
    return Promise.all(files.map(async (file) => {
        const response = await fetch(file.url);
        if (!response.ok) {
            throw new Error(`A(z) ${file.version} verzió újdonságainak betöltése sikertelen.`);
        }
        return {version: file.version, text: await response.text()};
    }));
}

export interface ReleaseNotesPopup {
    /** Newest first. */
    notes: ReleaseNote[];
    isOpen: boolean;
    /** Closes the popup and remembers its notes as seen. */
    dismiss: () => void;
}

/**
 * State of the "what's new" popup. The unseen notes are picked once, on mount, from what the user
 * acknowledged before; they are downloaded only if there are any, and the popup opens once they
 * have loaded. They count as seen when the popup is closed, so a reload before that (or a failed
 * download) shows them again.
 */
export function useReleaseNotes(): ReleaseNotesPopup {
    // A lazy initializer: runs on the first render only. It must stay free of side effects, since
    // React may render more than once for a single mount (StrictMode does so on purpose).
    const [unseen] = useState(() => selectUnseenNotes(RELEASE_NOTE_FILES, version(), readSeenVersion()));
    const [dismissed, setDismissed] = useState(false);
    const query = useQuery({
        queryKey: ['release-notes', unseen.map((file) => file.version)],
        queryFn: () => fetchReleaseNotes(unseen),
        enabled: unseen.length > 0,
        staleTime: Infinity,
    });

    const dismiss = () => {
        if (unseen.length) writeSeenVersion(unseen[0].version);
        setDismissed(true);
    };

    const notes = query.data ?? [];
    return {notes, isOpen: notes.length > 0 && !dismissed, dismiss};
}
